import { HiLightningBolt, HiSparkles } from "react-icons/hi";

import { useState } from "react";
import styled from "styled-components";

import { NovuComponent } from "MVVM/Components/NovuComponent";
import { Modal } from "MVVM/Components/Wrappers/Existing/modal";
import { TrialCard } from "MVVM/Components/SiteHeader/UpgradeCard";
import { useUser } from "MVVM/Hooks/useUser";
import { useSubscriptionInfo } from "MVVM/Hooks/useHasProAccess";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { URLS } from "MVVM/Utilities/Maps";
import { isPathActive, superNavLinks } from "../links";
import { UserSearch } from "../../UserSearch";
import { UserMenu } from "../UserMenu";
import { useTrialCta } from "../UserMenu/useTrialCta";
import { TrialButton } from "../UserMenu/styledComponents";
import {
  BurgerButton,
  NavDivider,
  SearchSlot,
  SuperNavLink,
  SuperNavLinks,
  LogoLink,
  StyledHeaderLogo,
  StyledHiMenu,
  SuperNavEnd,
  SuperNavInner,
  SuperNavRight,
  SuperNavStrip,
} from "../styledComponents";

/**
 * The permanent top strip: brand, search and identity.
 *
 * Present in both auth states, which is what stops search and the account
 * controls from relocating the moment you sign in. Signing in swaps the auth
 * buttons for the avatar and adds the bell; nothing moves.
 */
// The card has no width of its own; in the dropdown its container supplies one,
// so in a modal it collapsed to its content.
const TrialModalSlot = styled.div`
  width: min(440px, calc(100vw - 32px));
`;

export const SuperNav = ({
  isLoggedIn,
  pathname,
  standalone,
  onOpenMobile,
}: {
  isLoggedIn: boolean;
  pathname: string;
  standalone?: boolean;
  onOpenMobile: () => void;
}) => {
  const { allstarUser } = useUser();
  const user = allstarUser?.user;
  const links = superNavLinks(user?.profile?.username || user?.username);
  const trial = useTrialCta();
  const { isProOrHigher } = useSubscriptionInfo();
  const { track } = useAnalyticsContext();
  // The old sidebar carried a permanent Upgrade link, trial or no trial. Quiet
  // styling here so the trial button stays the only loud CTA in the strip.
  const showUpgrade = isLoggedIn && !isProOrHigher;
  const [trialOpen, setTrialOpen] = useState(false);

  return (
    <SuperNavStrip $standalone={standalone}>
      <SuperNavInner $standalone={standalone}>
        <BurgerButton onClick={onOpenMobile} aria-label="Open navigation">
          <StyledHiMenu />
        </BurgerButton>
        <LogoLink to="/" aria-label="Allstar home">
          <StyledHeaderLogo />
        </LogoLink>
        {/* Marketing pages sell rather than search, matching the old standalone
          header. */}
        {!standalone && (
          <SearchSlot>
            <UserSearch compact />
          </SearchSlot>
        )}
        <SuperNavEnd>
          {isLoggedIn && (
            <>
              <SuperNavLinks aria-label="Your shortcuts">
                {links.map((link) => {
                  const Icon = link.icon;
                  const active = isPathActive(link.to, pathname);

                  return (
                    <SuperNavLink
                      key={link.to}
                      to={link.to}
                      $active={active}
                      aria-current={active ? "page" : undefined}
                    >
                      <Icon aria-hidden="true" />
                      {link.label}
                    </SuperNavLink>
                  );
                })}
                {showUpgrade && (
                  <SuperNavLink
                    to={URLS.UPGRADE}
                    $active={isPathActive(URLS.UPGRADE, pathname)}
                    onClick={() => track("Nav Upgrade Clicked")}
                  >
                    <HiSparkles aria-hidden="true" />
                    Upgrade
                  </SuperNavLink>
                )}
              </SuperNavLinks>
              {/* The row changes meaning here: destinations to the left of it,
                account controls to the right. Signed out there are no
                shortcuts, so there is nothing to divide. */}
              <NavDivider aria-hidden="true" />
            </>
          )}
          <SuperNavRight>
            {/* Ahead of the bell: it is the one thing here asking for a decision,
              and it belongs with the account block it upsells. */}
            {trial.eligible && (
              <TrialButton
                type="button"
                onClick={() => {
                  track("Nav Trial CTA Clicked");
                  setTrialOpen(true);
                }}
              >
                <HiLightningBolt aria-hidden="true" />
                {trial.label}
              </TrialButton>
            )}
            {/* Nothing to notify a visitor about. */}
            {isLoggedIn && <NovuComponent />}
            {/* Renders the auth buttons signed out and the avatar signed in, so the
            slot itself never moves. */}
            <UserMenu />
          </SuperNavRight>
        </SuperNavEnd>
      </SuperNavInner>
      {/* The same card the old dropdown shows, so the trial is explained before
        the payment sheet rather than after. */}
      <Modal
        isModalOpen={trialOpen}
        setModalOpen={setTrialOpen}
        handleModalClose={undefined}
        disableOutsideClose={false}
      >
        <TrialModalSlot>
          <TrialCard trackAs="Nav Trial Modal CTA Clicked" showLearnMore />
        </TrialModalSlot>
      </Modal>
    </SuperNavStrip>
  );
};
